import { cn } from '@/utlis/cn'
import { Dialog as RadDialog, VisuallyHidden } from 'radix-ui'
import { CircleIconButton } from '@/UIComponents2/CircleIconButton'
import ClearIcon from '@/CustomIcons/ClearIcon'
import { useContext } from 'react'
import { PortalContext } from '@/UIComponents2/PortalContext'

type DialogProps = {
    title?: string
    className?: string
    children?: React.ReactNode
    open: boolean
    hideCloseButton?: boolean
    noDefaultScroll?: boolean
    zIndex?: number
    portaled?: boolean
    onClose: (reason: 'unfocus' | 'close-button') => void
    onCloseButtonClick?: () => void
}

const dialogContainerClasses = `flex relative overflow-hidden bg-[#262626] rounded-[32px] border-muse-toplit p-2 pt-[40px] bg-[url('/images/sparkle-short.webp')] bg-repeat-x bg-top`
const webDialogContainerClasses = `${dialogContainerClasses} w-[90vw] md:w-[450px] max-h-[80vh]`
const nativeDialogContainerClasses = `${dialogContainerClasses} min-w-[450px] h-full`

/**
 * Web dialog with the following provided:
 * - Background image/color, rounded edge, and padding
 * - Close button in top right corner
 * - Default width `w-[90vw] md:w-[510px]`
 * - Default height matching content, with `max-h-[80vh]`
 *
 * Overrideable width and height classes:
 * - `w-[90vw]`
 * - `md:w-[510px]`
 * - `max-h-[80vh]`
 */
export function Dialog(props: DialogProps) {
    const z = props.zIndex ?? 20
    const portaled = props.portaled ?? true
    const portalContainer = useContext(PortalContext)

    const content = (
        <>
            <RadDialog.Overlay className="fixed inset-0 bg-black/30 backdrop-blur-[5px]" style={{ zIndex: z }} />
            {props.open && (
                <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: z + 1 }}>
                    <RadDialog.Content className={cn(webDialogContainerClasses, props.className)}>
                        <VisuallyHidden.Root>
                            <RadDialog.Title>{props.title}</RadDialog.Title>
                        </VisuallyHidden.Root>
                        {props.noDefaultScroll ? (
                            <>{props.children}</>
                        ) : (
                            <div className="overflow-auto min-w-full min-h-full">{props.children}</div>
                        )}

                        {!props.hideCloseButton && (
                            <div className="absolute top-0 right-0 left-0 flex justify-end pt-3 pr-3">
                                <RadDialog.Close asChild>
                                    <CircleIconButton onClick={() => props.onClose('close-button')}>
                                        <ClearIcon style={{ fontSize: '32px' }} />
                                    </CircleIconButton>
                                </RadDialog.Close>
                            </div>
                        )}
                    </RadDialog.Content>
                </div>
            )}
        </>
    )
    return (
        <RadDialog.Root open={props.open} onOpenChange={(open) => !open && props.onClose('unfocus')}>
            {portaled ? <RadDialog.Portal container={portalContainer}>{content}</RadDialog.Portal> : content}
        </RadDialog.Root>
    )
}

type DialogContentProps = {
    className?: string
    children?: React.ReactNode
    hideCloseButton?: boolean
    noDefaultScroll?: boolean
    onClose?: () => void
}
/**
 * Dialog content for native dialogs with the following provided:
 * - Background image/color, rounded edge, and padding
 * - Close button in top right corner
 * - Default width `min-w-[510px]`
 * - Default height `h-full`
 *
 * The `min-w` ensures that the native dialog starts out with a default width,
 * but allows the div to resize when the native dialog is resized.
 *
 * Overrideable width and height classes:
 * - `min-w-[510px]`
 * - `h-full`
 */
export function NativeDialogContent(props: DialogContentProps) {
    return (
        <div className={cn(nativeDialogContainerClasses, props.className)}>
            {props.noDefaultScroll ? (
                <>{props.children}</>
            ) : (
                <div className="overflow-auto min-w-full min-h-full">{props.children}</div>
            )}
            {!props.hideCloseButton && (
                <div className="absolute top-0 right-0 left-0 flex justify-end app-window-drag-region pt-3 pr-3">
                    <CircleIconButton onClick={props.onClose}>
                        <ClearIcon style={{ fontSize: '32px' }} />
                    </CircleIconButton>
                </div>
            )}
        </div>
    )
}

export function DialogTitle({
    children,
    noPadding,
    className,
}: {
    children: React.ReactNode
    noPadding?: boolean
    className?: string
}) {
    return <h2 className={cn('t-title-dialog', noPadding ? 'pb-0' : 'pb-4', className)}>{children}</h2>
}

export function DialogSubtitle({ children, className }: { children: React.ReactNode; className?: string }) {
    return <h3 className={cn('t-subtitle-dialog', className)}>{children}</h3>
}

export function DialogBody({ children, className }: { children: React.ReactNode; className?: string }) {
    return <div className={cn('px-10 w-full overflow-auto', className)}>{children}</div>
}
